import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { displayName, useSocialStore } from '@/store/social'
import { describeError } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { Avatar } from '../Avatar'
import { isOnline } from '@/lib/presence'
import { avatarUrl } from '@/lib/avatar'
import { Modal } from '../settings/Modal'
import styles from './FriendPicker.module.css'

interface Props {
  title: string
  submitLabel: (count: number) => string
  /** Кого не показывать: уже участники группы. */
  exclude?: string[]
  /** Сколько ещё можно выбрать. */
  max: number
  min?: number
  onSubmit: (ids: string[]) => Promise<void>
  onClose: () => void
}

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Выбор друзей для группового ЛС: поиск, галочки, лимит участников. */
export function FriendPicker({ title, submitLabel, exclude = [], max, min = 1, onSubmit, onClose }: Props) {
  const relationships = useSocialStore((s) => s.relationships)
  const presences = useSocialStore((s) => s.presences)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const friends = useMemo(() => {
    const q = query.trim().toLowerCase()
    return Object.values(relationships)
      .filter((r) => r.type === 1 && !exclude.includes(r.id))
      .filter((r) => !q || r.user.username.includes(q) || displayName(r.user).toLowerCase().includes(q))
      .sort((a, b) => displayName(a.user).localeCompare(displayName(b.user), 'ru'))
  }, [relationships, exclude, query])

  const toggle = (id: string) => {
    setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : list.length < max ? [...list, id] : list))
  }
  const left = max - selected.length
  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await onSubmit(selected)
      onClose()
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <p className={styles.hint}>{left > 0 ? `Можно выбрать ещё: ${left}` : 'Достигнут предел участников группы'}</p>
      <label className={styles.search}>
        <input className={styles.searchInput} placeholder="Найти друга" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Поиск по друзьям" autoFocus />
        <Search size={18} className={styles.searchIcon} />
      </label>
      <ul className={cx(styles.list, 'scroller')}>
        {friends.length === 0 ? <li className={styles.empty}>{exclude.length ? 'Все ваши друзья уже в группе.' : 'Сначала добавьте друзей — в группу можно звать только их.'}</li> : null}
        {friends.map((r) => {
          const checked = selected.includes(r.id)
          const name = displayName(r.user)
          return (
            <li key={r.id}>
              <label className={cx(styles.row, checked && styles.rowChecked)}>
                <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(r.id)} size={32} status={isOnline(presences[r.id]) ? presences[r.id] : 'offline'} className={styles.avatar} src={avatarUrl(r.user.id, r.user.avatar)} decoration={r.user.avatar_decoration_data} />
                <span className={styles.name}>{name} <span className={styles.username}>@{r.user.username}</span></span>
                <input type="checkbox" className={styles.check} checked={checked} disabled={!checked && left === 0} onChange={() => toggle(r.id)} aria-label={`Выбрать ${name}`} />
              </label>
            </li>
          )
        })}
      </ul>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <button type="button" className={styles.submit} disabled={busy || selected.length < min} onClick={() => void submit()}>
        {busy ? 'Секунду…' : submitLabel(selected.length)}
      </button>
    </Modal>
  )
}
